import type { PropsWithChildren } from 'react';

import { NavLink } from 'react-router-dom';

import { useTheme } from '@/app/providers/ThemeProvider';

export function AppShell({ children }: PropsWithChildren): React.JSX.Element {
  const { isDark, toggleTheme } = useTheme();

  return (
    <div className="ui-layout-shell flex min-h-screen w-full flex-col gap-8 px-4 py-8 md:px-8">
      <header className="rounded-panel border border-base-300 bg-base-100/90 px-6 py-5 shadow-panel backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-base-content/70">
              Starknet data plane
            </p>
            <h1 className="font-display text-2xl font-semibold tracking-tight md:text-3xl">
              Mezcal Explorer
            </h1>
            <nav className="mt-3 flex flex-wrap items-center gap-2">
              <NavItem label="Explorer" to="/" />
              <NavItem label="Demo" to="/demo" />
              <NavItem label="Contract" to="/contract" />
              <NavItem label="Query" to="/query" />
              <NavItem label="Assistant" to="/assistant" />
              <NavItem label="Proofs" to="/proofs" />
              <NavItem label="Trace" to="/trace" />
              <NavItem label="Load test" to="/load-test" />
              <NavItem label="Docs" to="/docs" />
            </nav>
          </div>
          <button
            aria-checked={isDark}
            aria-label="Toggle light and dark theme"
            className="relative grid h-11 w-44 grid-cols-2 rounded-full border border-base-300 bg-base-200/80 p-1 font-mono text-[11px] uppercase tracking-[0.12em] text-base-content/70"
            onClick={toggleTheme}
            role="switch"
            type="button"
          >
            <span
              className={`pointer-events-none absolute left-1 top-1 h-9 w-[calc(50%-0.25rem)] rounded-full border border-base-300 bg-base-100/95 shadow-panel transition-transform duration-200 ease-out ${isDark ? 'translate-x-full' : 'translate-x-0'}`}
            />
            <span
              className={`relative z-10 flex items-center justify-center ${isDark ? '' : 'text-base-content'}`}
            >
              Light
            </span>
            <span
              className={`relative z-10 flex items-center justify-center ${isDark ? 'text-base-content' : ''}`}
            >
              Dark
            </span>
          </button>
        </div>
      </header>
      <main className="w-full pb-16">{children}</main>
    </div>
  );
}

function NavItem({ label, to }: { label: string; to: string }): React.JSX.Element {
  return (
    <NavLink
      className={({ isActive }) =>
        `rounded-panel border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] transition-colors ${
          isActive
            ? 'border-primary/50 bg-primary/15 text-primary'
            : 'border-base-300 text-base-content/70 hover:bg-base-200/70'
        }`
      }
      end={to === '/'}
      to={to}
    >
      {label}
    </NavLink>
  );
}
